import { ref, computed, watch, type Ref } from "vue";
import Report from "@/services/analytics/report";
import { QUICK_INSIGHT_PROMPTS } from "@/utils/quick-insight-prompts";

interface AIChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  quickInsightCategory: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

interface AIChatConversation {
  id: string;
  channel: string;
  month: string;
  title: string | null;
  messages: AIChatMessage[];
  createdAt: string;
}

function createTempUserMsg(content: string, category?: string): AIChatMessage {
  return {
    id: `temp-${Date.now()}`,
    role: "user",
    content,
    quickInsightCategory: category ?? null,
    metadata: null,
    createdAt: new Date().toISOString(),
  };
}

function replaceTempMsg(messages: AIChatMessage[], temp: AIChatMessage, server: AIChatMessage) {
  const idx = messages.indexOf(temp);
  if (idx !== -1) messages.splice(idx, 1, server);
}

export function useAIChat(channel: Ref<string>, month: Ref<string>) {
  const conversation = ref<AIChatConversation | null>(null);
  const messages = ref<AIChatMessage[]>([]);
  const isSending = ref(false);
  const isLoading = ref(false);
  let abortController: AbortController | null = null;

  const hasMessages = computed(() => messages.value.length > 0);

  async function loadExistingConversation() {
    isLoading.value = true;
    try {
      const res = await Report.listAIChatConversations({
        channel: channel.value,
        month: month.value,
      });
      const list = res.data?.data ?? [];
      if (list.length > 0) {
        const convRes = await Report.getAIChatConversation(list[0].id);
        const conv = convRes.data?.data;
        if (conv) {
          conversation.value = conv;
          messages.value = conv.messages ?? [];
        }
      }
    } catch {
      // No existing conversation — start fresh
    } finally {
      isLoading.value = false;
    }
  }

  async function ensureConversation(): Promise<string> {
    if (conversation.value) return conversation.value.id;
    const res = await Report.createAIChatConversation({
      channel: channel.value,
      month: month.value,
    });
    const conv = res.data?.data;
    conversation.value = { ...conv, messages: [] };
    return conv.id;
  }

  async function sendMessage(content: string) {
    if (isSending.value || !content.trim()) return;
    isSending.value = true;

    const tempUserMsg = createTempUserMsg(content.trim());
    messages.value.push(tempUserMsg);

    abortController = new AbortController();
    try {
      const conversationId = await ensureConversation();
      const res = await Report.sendAIChatMessage(
        conversationId,
        { content: content.trim() },
        abortController.signal
      );
      const data = res.data?.data;
      if (data?.userMessage) replaceTempMsg(messages.value, tempUserMsg, data.userMessage);
      if (data?.assistantMessage) messages.value.push(data.assistantMessage);

      if (conversation.value && !conversation.value.title) {
        conversation.value.title = content.trim().substring(0, 100);
      }
    } catch (err: any) {
      if (err?.code === "ERR_CANCELED") return;
      const errorMsg = err?.response?.data?.msg || "Failed to get AI response. Please try again.";
      messages.value.push({
        id: `error-${Date.now()}`,
        role: "assistant",
        content: errorMsg,
        quickInsightCategory: null,
        metadata: null,
        createdAt: new Date().toISOString(),
      });
    } finally {
      abortController = null;
      isSending.value = false;
    }
  }

  async function sendQuickInsight(category: string, label?: string) {
    if (isSending.value) return;
    isSending.value = true;

    const fullPrompt = QUICK_INSIGHT_PROMPTS[channel.value]?.[category] || label || category;
    const tempUserMsg = createTempUserMsg(fullPrompt, category);
    messages.value.push(tempUserMsg);

    abortController = new AbortController();
    try {
      const conversationId = await ensureConversation();
      const res = await Report.sendAIChatMessage(
        conversationId,
        { quickInsightCategory: category },
        abortController.signal
      );
      const data = res.data?.data;
      if (data?.userMessage) replaceTempMsg(messages.value, tempUserMsg, data.userMessage);
      if (data?.assistantMessage) messages.value.push(data.assistantMessage);
    } catch (err: any) {
      if (err?.code === "ERR_CANCELED") return;
      const errorMsg = err?.response?.data?.msg || "Failed to get AI response. Please try again.";
      messages.value.push({
        id: `error-${Date.now()}`,
        role: "assistant",
        content: errorMsg,
        quickInsightCategory: null,
        metadata: null,
        createdAt: new Date().toISOString(),
      });
    } finally {
      abortController = null;
      isSending.value = false;
    }
  }

  function resetConversation() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    isSending.value = false;
    conversation.value = null;
    messages.value = [];
  }

  watch([channel, month], () => {
    resetConversation();
    loadExistingConversation();
  });

  return {
    conversation,
    messages,
    isSending,
    isLoading,
    hasMessages,
    sendMessage,
    sendQuickInsight,
    resetConversation,
    loadExistingConversation,
  };
}
